import { pipe, catchError } from 'overmind';

import type { CalculationState } from '@/features/execute-calculation-set/overmind/calculation';
import type { Context } from '@/overmind';
import type { GUID } from '@/overmind/effects/auth';
import { getRequestAborter } from '@/overmind/effects/requestAborter';
import { UnexpectedStateError, AbortError } from '@/overmind/errors';
import { routes } from '@/overmind/machines';

const { switchToNewRequest, completeRequestOperator } =
  getRequestAborter('getArcGISToken');

export const register = pipe(
  async ({ state, effects }: Context) => {
    const abortController = switchToNewRequest();
    if (
      state.tenants.availableTenants.type === 'AVAILABLE' &&
      state.tenants.currentTenant.type === 'SELECTED_TENANT'
    ) {
      const externalArcGisToken = getExternalArcGisToken(state.calculation);
      if (externalArcGisToken != null) {
        return externalArcGisToken;
      }
      const tenant = state.tenants.currentTenant.context.tenantId;
      return effects.api.networkService.getArcGisToken(tenant, abortController);
    }

    throw new UnexpectedStateError();
  },
  ({ state }: Context, arcGisToken) => {
    state.auth.send('ARCGIS_AUTHENTICATION_COMPLETED', arcGisToken);
  },
  catchError((_: Context, error) => {
    if (
      !(error instanceof UnexpectedStateError || error instanceof AbortError)
    ) {
      // ts-ignore
    }
  }),
  completeRequestOperator()
);

export const signInArcGis = pipe(async (context: Context) => {
  if (context.state.arcgis.config.current === 'LOADED') {
    /**
           when doing redirect to ArcGIS Portal to sign in
          web app current route needs to match the redirect_uri
          configured in the ArcGIS Portal
          */
    if (context.state.config.type === 'AVAILABLE') {
      const applicationId = context.state.config.config.auth
        .applicationId as GUID;
      const tenantId = await context.actions.tenants.getTenantForUserSession({
        applicationId
      });
      context.actions.tenants.trySavingTenant({ tenantId });
    }

    context.effects.router.replaceUrl('/signin-arcgis');
    const config = context.state.arcgis.config.value;
    await context.effects.arcgis.auth.identity.signIn(config);
    const token = context.effects.arcgis.auth.identity.getToken(
      config
    ) as string;
    const isAlreadyLoggedIn = Boolean(token);
    if (isAlreadyLoggedIn) {
      const calculationContext =
        context.effects.storage.get('calculationContext');
      if (calculationContext) {
        calculationContext.arcgisToken = token;
        context.effects.storage.set('calculationContext', undefined);
        context.effects.router.goTo({
          url: routes.executeCalculationSetUsing,
          query: { ...calculationContext }
        });
      } else {
        context.effects.router.goTo({ url: routes.root });
      }
    }
    context.state.auth.send('ARCGIS_AUTHENTICATING');
    context.state.auth.send('ARCGIS_AUTHENTICATION_COMPLETED', token);
    return;
  }

  throw new UnexpectedStateError();
}, completeRequestOperator());

export const isAuthenticated = async (context: Context) => {
  if (context.state.arcgis.config.current === 'LOADED') {
    const config = context.state.arcgis.config.value;
    return await context.effects.arcgis.auth.identity.isAuthenticated(config);
  }
  return false;
};

function getExternalArcGisToken(state: CalculationState) {
  return state.externallyTriggeredCalculationSetExecution.matches('READY')
    ?.calculationContext.arcgisToken;
}
